import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '@/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Building2, CalendarDays, CheckCircle2, Clock3, TrendingUp, UserCircle2 } from 'lucide-react';

const stageColors: Record<string, string> = {
  Prospecto: 'bg-[#F1F5F9] text-[#475569] border-[#94A3B8]/30',
  'Contacto realizado': 'bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20',
  'Reunion / demo': 'bg-[#ECFEFF] text-[#0F766E] border-[#14B8A6]/20',
  'Propuesta enviada': 'bg-[#FFFBEB] text-[#B45309] border-[#F59E0B]/20',
  Negociacion: 'bg-[#F5F3FF] text-[#7C3AED] border-[#8B5CF6]/20',
  'Cerrado ganado': 'bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20',
  'Cerrado perdido': 'bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/20',
};

const healthColors: Record<string, string> = {
  saludable: 'bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20',
  riesgo: 'bg-[#FFFBEB] text-[#B45309] border-[#F59E0B]/20',
  alto_riesgo: 'bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/20',
};

function formatCLP(n: number) {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(n || 0);
}

export default function OpportunityDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      const [oppRes, historyRes, activitiesRes] = await Promise.all([
        api.get(`/opportunities/${id}`),
        api.get(`/opportunities/${id}/history`),
        api.get('/activities'),
      ]);
      setOpportunity(oppRes.data);
      setHistory(historyRes.data || []);
      setActivities((activitiesRes.data || []).filter((item: any) => item.opportunity_id === id));
    };
    fetchData().catch(() => setOpportunity(null));
  }, [id]);

  const summary = useMemo(() => ({
    forecastAmount: Math.round(Number(opportunity?.amount || 0) * (Number(opportunity?.probability || 0) / 100)),
    hasControlGap: !opportunity?.next_step || !opportunity?.next_step_date,
  }), [opportunity]);

  if (!id) return null;
  if (!opportunity) return <div className="p-6 text-sm text-slate-500">Oportunidad no encontrada.</div>;

  return (
    <div className="space-y-6" data-testid="opportunity-detail-page">
      <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur">
        <div className="h-1.5 bg-gradient-to-r from-[#8B5CF6] via-[#F59E0B] to-[#22C55E]" />
        <div className="flex flex-col gap-4 p-5 md:p-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-3 min-h-11 gap-2 text-[#475569] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:min-h-0">
              <ArrowLeft size={16} /> Volver
            </Button>
            <h1 className="text-2xl font-bold text-[#0F172A] break-words md:text-3xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{opportunity.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {opportunity.account_id ? (
                <Link to={`/empresas/${opportunity.account_id}`} className="max-w-full rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2">
                  <Badge className="max-w-full truncate bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20 hover:bg-[#DBEAFE] cursor-pointer">{opportunity.account_name}</Badge>
                </Link>
              ) : (
                <Badge className="bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20">Sin empresa</Badge>
              )}
              <Badge variant="outline" className={`max-w-full truncate px-3 py-1 text-xs font-semibold ${stageColors[opportunity.stage] || ''}`}>{opportunity.stage}</Badge>
              <Badge className="bg-[#F8FAFC] text-[#475569] border-[#E2E8F0] tabular-nums">{opportunity.probability}%</Badge>
              <Badge variant="outline" className={`px-3 py-1 text-xs font-semibold ${healthColors[opportunity.health_band] || 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'}`}>
                Salud {typeof opportunity.health_score === 'number' ? opportunity.health_score : '-'}
              </Badge>
            </div>
            <p className="mt-2 text-sm text-[#475569]">Detalle operativo con control comercial, forecast y trazabilidad real.</p>
          </div>
          <div className="grid w-full grid-cols-2 gap-3 lg:w-auto lg:grid-cols-4">
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Neto</p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-[#0F172A]">{formatCLP(opportunity.monto_neto || 0)}</p>
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">IVA</p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-[#0F172A]">{formatCLP(opportunity.iva || 0)}</p>
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Bruto</p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-[#0F172A]">{formatCLP(opportunity.total_bruto || opportunity.amount || 0)}</p>
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Forecast</p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-[#0F172A]">{formatCLP(summary.forecastAmount)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2 overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-gradient-to-r from-[#2563EB] via-[#8B5CF6] to-[#22C55E]" />
          <CardHeader><CardTitle>Control comercial</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-3 text-sm">
            <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
              <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Responsable</p>
              <p className="mt-2 font-semibold text-[#0F172A]">{opportunity.owner_name || 'Sin asignar'}</p>
            </div>
            <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
              <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Próximo paso</p>
              <p className="mt-2 font-semibold text-[#0F172A]">{opportunity.next_step || 'Sin definir'}</p>
              <p className="text-xs text-[#64748B]">{opportunity.next_step_date || 'Sin fecha'}</p>
            </div>
            <div className={`rounded-lg border p-3 ${summary.hasControlGap ? 'border-[#FECACA] bg-[#FEF2F2]' : 'border-[#DCFCE7] bg-[#F0FDF4]'}`}>
              <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Salud del pipeline</p>
              <p className="mt-2 font-semibold text-[#0F172A]">{summary.hasControlGap ? 'Falta control' : 'Completa'}</p>
              <p className="text-xs tabular-nums text-[#64748B]">{opportunity.days_in_stage || 0} días en etapa</p>
            </div>
            <div className="rounded-lg border border-[#E2E8F0] bg-white p-3">
              <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Salud comercial</p>
              <div className="mt-2 flex items-center gap-2">
                <Badge variant="outline" className={`text-xs font-semibold ${healthColors[opportunity.health_band] || 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'}`}>
                  {opportunity.health_band || 'n/a'}
                </Badge>
                <span className="text-sm font-semibold tabular-nums text-[#0F172A]">{typeof opportunity.health_score === 'number' ? `${opportunity.health_score}/100` : '-'}</span>
              </div>
              <p className="mt-2 text-xs text-[#475569]">{opportunity.health_recommendation || 'Sin recomendación'}</p>
              {Array.isArray(opportunity.health_reasons) && opportunity.health_reasons.length > 0 && (
                <div className="mt-2 space-y-1 text-xs text-[#64748B]">
                  {opportunity.health_reasons.slice(0, 6).map((r: string, idx: number) => (
                    <div key={idx}>- {r}</div>
                  ))}
                </div>
              )}
              <div className="mt-3">
                <Button size="sm" variant="outline" className="min-h-11 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:min-h-0" onClick={() => navigate('/actividades')}>
                  Crear actividad
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-gradient-to-r from-[#0EA5E9] via-[#2563EB] to-[#8B5CF6]" />
          <CardHeader><CardTitle>Vinculos</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center gap-2 text-[#475569]"><Building2 size={15} className="text-[#2563EB]" /> {opportunity.account_name || '-'}</div>
            <div className="flex items-center gap-2 text-[#475569]"><UserCircle2 size={15} className="text-[#8B5CF6]" /> {opportunity.contact_name || '-'}</div>
            <div className="flex items-center gap-2 text-[#475569]"><CalendarDays size={15} className="text-[#F59E0B]" /> {opportunity.close_date || '-'}</div>
            <div className="flex items-center gap-2 text-[#475569]"><TrendingUp size={15} className="text-[#22C55E]" /> {opportunity.probability}% probabilidad</div>
            <div className="flex items-center gap-2 text-[#475569]"><Clock3 size={15} className="text-[#94A3B8]" /> Última actividad: {opportunity.last_activity_at ? opportunity.last_activity_at.slice(0, 10) : 'Sin actividad'}</div>
            {opportunity.lost_reason && (
              <div className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-3 text-[#B91C1C]">
                Motivo de pérdida: {opportunity.lost_reason}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-gradient-to-r from-[#F59E0B] via-[#8B5CF6] to-[#2563EB]" />
          <CardHeader><CardTitle>Trazabilidad</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {history.map((item) => (
              <div key={item.id} className="rounded-lg border border-[#E2E8F0] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-[#0F172A]">{item.summary}</p>
                    <p className="text-xs text-[#64748B]">{item.actor_name || 'Sistema'} · {item.changed_at?.slice(0, 10)}</p>
                  </div>
                  {item.event_type === 'stage_changed' && <Badge className="bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20">Etapa</Badge>}
                  {item.event_type === 'probability_changed' && <Badge className="bg-[#F5F3FF] text-[#7C3AED] border-[#8B5CF6]/20">Probabilidad</Badge>}
                  {item.event_type === 'activity_logged' && <Badge className="bg-[#ECFDF5] text-[#15803D] border-[#22C55E]/20">Actividad</Badge>}
                </div>
              </div>
            ))}
            {history.length === 0 && <p className="text-sm text-[#94A3B8]">Sin eventos registrados.</p>}
          </CardContent>
        </Card>

        <Card className="overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-gradient-to-r from-[#22C55E] via-[#2563EB] to-[#8B5CF6]" />
          <CardHeader><CardTitle>Actividades</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {activities.map((activity) => (
              <div key={activity.id} className="rounded-lg border border-[#E2E8F0] p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-[#0F172A]">{activity.subject}</p>
                  {activity.status === 'completada' && <CheckCircle2 size={14} className="text-[#22C55E]" />}
                </div>
                <p className="text-xs text-[#64748B]">{activity.type} · {activity.status}</p>
                <p className="text-xs text-[#64748B]">{activity.due_date || activity.created_at?.slice(0, 10) || '-'}</p>
              </div>
            ))}
            {activities.length === 0 && <p className="text-sm text-[#94A3B8]">Sin actividades registradas.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
